import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchRelatedOrderPayment } from 'api/mdl11/relatedOrderPayment'
import {
  getRelatedWebSalesOrder,
  hasAssignedOrderPayment,
} from 'Utils/relatedOrderPayment'

export const useRelatedOrderPayment = (quote, initialOrder = null) => {
  const relatedOrder = getRelatedWebSalesOrder(quote)
  const orderId = relatedOrder?.DocHeaderId
  const orderNumber = relatedOrder?.DocumentNumber
  const needsCheck = Boolean(relatedOrder)
  const loadedOrder =
    initialOrder?.DocHeaderId === orderId ? initialOrder : null
  const [state, setState] = useState({})
  const requestId = useRef(0)

  const refresh = useCallback(async () => {
    const currentRequest = ++requestId.current
    if (!needsCheck) return null
    setState(previous => ({ ...previous, loading: true, error: null }))
    try {
      const order = await fetchRelatedOrderPayment({
        DocHeaderId: orderId,
        DocumentNumber: orderNumber,
      })
      if (currentRequest === requestId.current) {
        setState({ orderId, order, loading: false })
      }
      return order
    } catch (error) {
      if (currentRequest === requestId.current) {
        setState(previous => ({
          orderId,
          order: previous.orderId === orderId ? previous.order : null,
          error,
          loading: false,
        }))
      }
      throw error
    }
  }, [needsCheck, orderId, orderNumber])

  useEffect(() => {
    if (!needsCheck) return
    if (loadedOrder) {
      setState({ orderId, order: loadedOrder, loading: false })
    } else {
      refresh().catch(() => {})
    }
    return () => {
      requestId.current++
    }
  }, [needsCheck, orderId, loadedOrder, refresh])

  useEffect(() => {
    if (!needsCheck) return
    const refreshOnFocus = () => refresh().catch(() => {})
    window.addEventListener('focus', refreshOnFocus)
    return () => window.removeEventListener('focus', refreshOnFocus)
  }, [needsCheck, refresh])

  // Until the effect copies loadedOrder into state, use it directly so the
  // first render doesn't flash a loading state for data already in hand.
  const hasOrderState = state.orderId === orderId
  const order = needsCheck ? (hasOrderState ? state.order : loadedOrder) : null
  const locked = hasAssignedOrderPayment(order)
  return {
    order,
    locked,
    error: needsCheck ? state.error : null,
    loading:
      needsCheck && (hasOrderState ? Boolean(state.loading) : !loadedOrder),
    refresh,
  }
}
